import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useValuationStore } from '@/store/valuationStore'
import { useTourUiStore } from '@/store/tourStore'
import { Spotlight } from './Spotlight'
import { TourTooltip } from './TourTooltip'
import { WelcomeModal } from './WelcomeModal'
import type { TourStep } from './TourSteps'

interface TourProviderProps {
  steps: TourStep[]
  storageKey: string
}

const TOOLTIP_WIDTH = 320
const TOOLTIP_GAP = 20
const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024
const MAX_POLL_ATTEMPTS = 100 // ~10s at 100ms interval — generous enough for async ref-data loads after selecting a Vehicle Type

function computeTooltipStyle(rect: DOMRect, placement?: 'above'): CSSProperties {
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Anchored to the target's top via `bottom` (rather than computing an explicit `top` from
  // an assumed height) since the tooltip's real height varies with content — this way it
  // grows upward from the target instead of needing a height guess to avoid overlapping it.
  // Checked ahead of the mobile/tablet branches too: targets like the Save/Submit buttons sit
  // in a fixed bottom action bar, so the default mobile `bottom: 16` tooltip would land right
  // on top of them (both pinned to the bottom edge, tooltip on top by z-index).
  if (placement === 'above') {
    // Cap height to the actual space between the viewport top and the target, rather than
    // letting the tooltip grow upward to fit its content — a long (e.g. bulleted) description
    // can otherwise exceed that gap and push the title/description above y=0, leaving only the
    // footer buttons visible. Capping here makes the content area scroll internally instead.
    const availableHeight = Math.max(160, rect.top - TOOLTIP_GAP - 16)
    return {
      bottom: Math.max(16, vh - rect.top + TOOLTIP_GAP),
      left: vw < MOBILE_BREAKPOINT ? 16 : Math.max(16, Math.min(rect.left, vw - TOOLTIP_WIDTH - 16)),
      right: vw < MOBILE_BREAKPOINT ? 16 : undefined,
      width: vw < MOBILE_BREAKPOINT ? 'auto' : undefined,
      maxHeight: availableHeight,
    }
  }

  if (vw < MOBILE_BREAKPOINT) {
    return {
      left: 16,
      right: 16,
      bottom: 16,
      width: 'auto',
    }
  }

  if (vw < TABLET_BREAKPOINT) {
    return {
      top: Math.min(rect.bottom + TOOLTIP_GAP, vh - 240),
      left: Math.max(16, Math.min(rect.left, vw - TOOLTIP_WIDTH - 16)),
    }
  }

  // Desktop: prefer right of target, flip to left if it would overflow
  const fitsRight = rect.right + TOOLTIP_GAP + TOOLTIP_WIDTH < vw
  if (fitsRight) {
    return {
      top: Math.max(16, Math.min(rect.top, vh - 220)),
      left: rect.right + TOOLTIP_GAP,
    }
  }

  const fitsLeft = rect.left - TOOLTIP_GAP - TOOLTIP_WIDTH > 0
  if (fitsLeft) {
    return {
      top: Math.max(16, Math.min(rect.top, vh - 220)),
      left: rect.left - TOOLTIP_GAP - TOOLTIP_WIDTH,
    }
  }

  // Fallback: below target
  return {
    top: Math.min(rect.bottom + TOOLTIP_GAP, vh - 240),
    left: Math.max(16, Math.min(rect.left, vw - TOOLTIP_WIDTH - 16)),
  }
}

/** Ensures the given section's accordion is open, without re-collapsing an already-open one. */
function ensureSectionExpanded(sectionId: string) {
  const { sectionStates, toggleSection } = useValuationStore.getState()
  if (!sectionStates[sectionId]?.expanded) {
    toggleSection(sectionId)
  }
}

/** Some fields render a full-width sub-header divider directly above them (e.g. "Vehicle
 * Details"). If the spotlighted element has one as its immediately preceding sibling, fold
 * its bounds into the highlight box too — otherwise the divider's rule lines, which extend
 * well beyond the narrower field's own width, poke through the tighter cutout above it. */
function getSpotlightRect(el: Element): DOMRect {
  const rect = el.getBoundingClientRect()
  const prev = el.previousElementSibling
  if (!prev?.hasAttribute('data-tour-subheader')) return rect

  const prevRect = prev.getBoundingClientRect()
  return new DOMRect(
    Math.min(rect.left, prevRect.left),
    Math.min(rect.top, prevRect.top),
    Math.max(rect.right, prevRect.right) - Math.min(rect.left, prevRect.left),
    Math.max(rect.bottom, prevRect.bottom) - Math.min(rect.top, prevRect.top)
  )
}

export function TourProvider({ steps, storageKey }: TourProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const [active, setActive] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [interactiveRect, setInteractiveRect] = useState<DOMRect | null>(null)
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)
  // State (not just a ref) so the resize-sync effect below can depend on target identity
  // rather than on `rect` — depending on `rect` there caused an infinite loop: each re-run
  // created a fresh ResizeObserver, whose mandatory initial fire on `.observe()` updated
  // `rect` again, re-triggering the effect indefinitely and showing up as a constant
  // sub-pixel shake in the tooltip position.
  const [targetEl, setTargetEl] = useState<Element | null>(null)
  const [interactiveEl, setInteractiveEl] = useState<Element | null>(null)
  const targetElRef = useRef<Element | null>(null)
  const interactiveElRef = useRef<Element | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoAdvancePollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const waitBaselineRef = useRef<boolean | null>(null)

  const step = steps[stepIndex]

  const finishTour = useCallback(() => {
    localStorage.setItem(storageKey, 'true')
    setActive(false)
    targetElRef.current = null
    interactiveElRef.current = null
    navigate('/dashboard')
  }, [storageKey, navigate])

  // On first visit (not seen yet), show the welcome prompt instead of starting the tour
  // outright — the spotlight/dimming only begins once the user explicitly opts in.
  useEffect(() => {
    if (localStorage.getItem(storageKey)) return
    setShowWelcome(true)
  }, [storageKey])

  const handleStartTour = useCallback(() => {
    setShowWelcome(false)
    setActive(true)
  }, [])

  const handleSkipWelcome = useCallback(() => {
    localStorage.setItem(storageKey, 'true')
    setShowWelcome(false)
  }, [storageKey])

  // Show the welcome prompt again whenever the user requests a replay (e.g. a header button)
  // — the same entry point as a fresh first login, rather than jumping straight into the tour.
  //
  // Compares the signal's *value* rather than using a "have I run yet" boolean ref: React
  // StrictMode (dev only) double-invokes effects on mount, which defeated the boolean-ref
  // version — the first invocation flipped the guard, so the second invocation (still on the
  // same initial mount) saw it already consumed and wiped the tour-seen flag on every mount,
  // i.e. on every login. Comparing values is idempotent no matter how many times the effect
  // fires for an unchanged value.
  const restartSignal = useTourUiStore((s) => s.restartSignal)
  const lastHandledRestartSignal = useRef(restartSignal)
  useEffect(() => {
    if (restartSignal === lastHandledRestartSignal.current) return
    lastHandledRestartSignal.current = restartSignal
    localStorage.removeItem(storageKey)
    setActive(false)
    setBlockedMessage(null)
    setStepIndex(0)
    setShowWelcome(true)
  }, [restartSignal, storageKey])

  // Let other pages (e.g. FormPage's unsaved-changes navigation blocker) know the tour is
  // driving navigation, so they can treat it as intentional rather than accidental.
  useEffect(() => {
    useTourUiStore.getState().setTourActive(active)
    return () => { if (active) useTourUiStore.getState().setTourActive(false) }
  }, [active])

  const goNext = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1))
  }, [steps.length])

  const goPrev = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0))
  }, [])

  // The tooltip's own Next button — blocked (with a validation message) when the current
  // step requires some condition (e.g. a Vehicle Type) that hasn't been met yet, instead of
  // silently advancing past an incomplete step.
  const handleNextClick = useCallback(() => {
    // Only block manual Next when the step explicitly opts in with a validationMessage —
    // steps that merely auto-advance once ready (e.g. after an auto-selected Vehicle Type
    // finishes loading dependent sections) shouldn't stop the user from proceeding manually.
    if (step?.autoAdvanceWhen && step.validationMessage && !step.autoAdvanceWhen()) {
      setBlockedMessage(step.validationMessage)
      return
    }
    goNext()
  }, [step, goNext])

  // Clear any validation message whenever the active step changes
  useEffect(() => {
    setBlockedMessage(null)
  }, [step])

  // Locate the target element for the current step, waiting for cross-route mounts and
  // expanding its accordion section (if any) first, since a collapsed section's own
  // container may not reflect its full expanded size until opened.
  useEffect(() => {
    if (!active || !step) return

    targetElRef.current = null
    interactiveElRef.current = null
    setRect(null)
    setInteractiveRect(null)
    setTargetEl(null)
    setInteractiveEl(null)

    if (step.route && step.route !== location.pathname) {
      navigate(step.route)
      return
    }

    if (step.sectionId) ensureSectionExpanded(step.sectionId)

    let attempts = 0
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => {
      // Re-assert on every tick, not just once — the Form page's own "open first section on
      // mount" bootstrap effect can race our initial expand call and collapse it right back.
      if (step.sectionId) ensureSectionExpanded(step.sectionId)

      const el = document.querySelector(step.selector)
      attempts += 1
      if (el) {
        clearInterval(pollRef.current!)
        pollRef.current = null
        targetElRef.current = el
        step.onActivate?.()
        // 'auto' (instant) rather than 'smooth': a smooth scroll fires many scroll events
        // while in flight, each of which repositions the tooltip/spotlight mid-animation —
        // visible as the tooltip shaking/jittering while the page settles.
        // 'above'-placed steps scroll to the top of the page instead of centering the target —
        // centering would push the target (and the tooltip anchored above it) up past the
        // viewport, re-creating the very clipping this placement exists to avoid.
        if (step.placement === 'above') {
          window.scrollTo({ top: 0, behavior: 'auto' })
        } else {
          el.scrollIntoView({ behavior: 'auto', block: 'center' })
        }
        setRect(getSpotlightRect(el))
        setTargetEl(el)

        if (step.interactiveSelector) {
          const foundInteractiveEl = document.querySelector(step.interactiveSelector)
          interactiveElRef.current = foundInteractiveEl
          setInteractiveRect(foundInteractiveEl?.getBoundingClientRect() ?? null)
          setInteractiveEl(foundInteractiveEl)
        }
      } else if (attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(pollRef.current!)
        pollRef.current = null
        console.warn(`[Tour] Could not find target for step "${step.title}" (selector: ${step.selector}) after ${MAX_POLL_ATTEMPTS * 100}ms.`)
      }
    }, 100)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [active, step, location.pathname, navigate])

  // Keep the spotlight rect in sync as the page scrolls/resizes, and as the target itself
  // changes size — e.g. its accordion expanding, or dynamic fields loading in beneath it.
  useEffect(() => {
    if (!active || !targetEl) return
    const target = targetEl
    // Coalesce to one measurement per frame — scroll/resize/ResizeObserver can each fire many
    // times per frame (e.g. a table's own scrollbar, or a section's expand transition), and
    // committing every single one as a state update makes the tooltip visibly shake as it
    // chases each intermediate position instead of settling once per frame.
    let rafId: number | null = null
    const update = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        setRect(getSpotlightRect(target))
        if (interactiveElRef.current) setInteractiveRect(interactiveElRef.current.getBoundingClientRect())
      })
    }

    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(target)
    if (interactiveEl) resizeObserver.observe(interactiveEl)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      resizeObserver.disconnect()
    }
  }, [active, targetEl, interactiveEl])

  // Poll steps that auto-advance once some app state condition becomes true (e.g. a Vehicle
  // Type has been selected). Only fires on a false -> true transition observed *during this
  // step's active session* — otherwise navigating back to a step whose condition is already
  // satisfied (e.g. via Previous, after the Vehicle Type was already chosen) would instantly
  // bounce forward again, making Previous appear broken.
  useEffect(() => {
    if (!active || !step?.autoAdvanceWhen) return
    const predicate = step.autoAdvanceWhen
    let lastValue = predicate()
    if (autoAdvancePollRef.current) clearInterval(autoAdvancePollRef.current)
    autoAdvancePollRef.current = setInterval(() => {
      const current = predicate()
      if (!lastValue && current) {
        clearInterval(autoAdvancePollRef.current!)
        autoAdvancePollRef.current = null
        setBlockedMessage(null)
        goNext()
        return
      }
      lastValue = current
    }, 150)
    return () => {
      if (autoAdvancePollRef.current) {
        clearInterval(autoAdvancePollRef.current)
        autoAdvancePollRef.current = null
      }
    }
  }, [active, step, goNext])

  // Reset the "wait for real navigation" baseline whenever the active step changes, capturing
  // whether we're already on the target route at that moment.
  useEffect(() => {
    waitBaselineRef.current = step?.waitForRoutePrefix
      ? location.pathname.startsWith(step.waitForRoutePrefix)
      : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // Some steps hand off to a real click (e.g. New Claim) rather than the tooltip's own Next
  // button, since only the real click runs the app's own setup logic (e.g. clearing the
  // previous draft) that a plain `navigate()` call would skip. Advance once the app
  // navigates to the expected route — but only on a false -> true transition, not simply
  // because the route already matches. Without this, navigating back (Previous) to this step
  // while still physically on that route (before our own navigate-away below completes) would
  // instantly re-advance forward again, making Previous appear broken.
  useEffect(() => {
    if (!active || !step?.waitForRoutePrefix) return
    const matches = location.pathname.startsWith(step.waitForRoutePrefix)
    if (waitBaselineRef.current === false && matches) {
      waitBaselineRef.current = null
      goNext()
    } else {
      waitBaselineRef.current = matches
    }
  }, [active, step, location.pathname, goNext])

  // Keyboard navigation
  useEffect(() => {
    if (!active) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finishTour()
      else if (e.key === 'ArrowRight' && !step?.waitForRoutePrefix) handleNextClick()
      else if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [active, step, handleNextClick, goPrev, finishTour])

  if (showWelcome) {
    return <WelcomeModal open onStart={handleStartTour} onSkip={handleSkipWelcome} />
  }

  if (!active || !step || !rect) return null

  const tooltipStyle = computeTooltipStyle(rect, step.placement)
  const isFirst = stepIndex === 0
  const isLast = stepIndex === steps.length - 1

  const interactiveMode: 'full' | DOMRect | null = step.waitForRoutePrefix
    ? 'full'
    : (step.interactiveSelector ? interactiveRect : null)

  return (
    <>
      <Spotlight rect={rect} interactive={interactiveMode} />
      <TourTooltip
        style={tooltipStyle}
        title={step.title}
        description={step.description}
        step={stepIndex + 1}
        total={steps.length}
        isFirst={isFirst}
        isLast={isLast}
        isWaitingForAction={!!step.waitForRoutePrefix}
        blockedMessage={blockedMessage}
        onPrev={goPrev}
        onNext={handleNextClick}
        onSkip={finishTour}
        onFinish={finishTour}
      />
    </>
  )
}
